import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';
import styles from './gerador.module.scss';
import {
    buildWaMeUrl,
    isValidBrLocalPhone,
    maskBrPhone,
    parseWaMeUrl,
} from './utils.ts';

type WhatsAppLinkFieldProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    hint?: string;
    disabled?: boolean;
};

const BrazilFlag: React.FC = () => (
    <svg viewBox="0 0 20 14" width="22" height="16" aria-hidden="true">
        <rect width="20" height="14" rx="2" fill="#009B3A" />
        <polygon points="10,1.4 18.4,7 10,12.6 1.6,7" fill="#FFDF00" />
        <circle cx="10" cy="7" r="3.05" fill="#002776" />
    </svg>
);

export const WhatsAppLinkField: React.FC<WhatsAppLinkFieldProps> = ({
    label,
    value,
    onChange,
    placeholder = 'https://',
    hint,
    disabled,
}) => {
    const [open, setOpen] = useState(false);
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const openModal = () => {
        const parsed = parseWaMeUrl(value);
        setPhone(parsed ? maskBrPhone(parsed.localDigits) : '');
        setMessage(parsed?.text ?? '');
        setError('');
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const handleOk = () => {
        if (!isValidBrLocalPhone(phone)) {
            setError('Digite DDD e número (10 ou 11 dígitos).');
            return;
        }
        onChange(buildWaMeUrl(phone, message));
        setOpen(false);
    };

    return (
        <div className={styles.field}>
            <span className={styles.fieldLabel}>{label}</span>
            <div className={styles.urlRow}>
                <input
                    type="url"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    disabled={disabled}
                />
                <button
                    type="button"
                    className={styles.waIconBtn}
                    onClick={openModal}
                    disabled={disabled}
                    aria-label="Montar link do WhatsApp"
                    title="Montar link do WhatsApp"
                >
                    <WhatsappIcon color="#25D366" height="20" />
                </button>
            </div>
            {hint ? <span className={styles.hint}>{hint}</span> : null}

            {open
                ? createPortal(
                    <div
                        className={styles.confirmOverlay}
                        onClick={() => setOpen(false)}
                        role="presentation"
                    >
                        <div
                            className={styles.confirmModal}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="whatsapp-title"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 id="whatsapp-title">Link do WhatsApp</h3>
                            <p>Informe o número com DDD. O texto da mensagem é opcional.</p>

                            <div className={styles.waPhoneRow}>
                                <div className={styles.waCountry}>
                                    <BrazilFlag />
                                    <span>+55</span>
                                </div>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel-national"
                                    className={styles.waPhoneInput}
                                    value={phone}
                                    onChange={(e) => {
                                        setPhone(maskBrPhone(e.target.value));
                                        setError('');
                                    }}
                                    placeholder="(17) 99210-8900"
                                    aria-label="Número com DDD"
                                />
                            </div>

                            <label className={styles.field}>
                                <span className={styles.fieldLabel}>
                                    Mensagem <em>opcional</em>
                                </span>
                                <input
                                    type="text"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Olá! Vim pelo link na bio."
                                />
                            </label>

                            {error ? <p className={styles.waError}>{error}</p> : null}

                            <div className={styles.confirmActions}>
                                <button type="button" className={styles.ghostBtn} onClick={() => setOpen(false)}>
                                    Cancelar
                                </button>
                                <button type="button" className={styles.primaryBtn} onClick={handleOk}>
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </div>
    );
};
